import type { RecaptionPrompt, ReferencePrompt, TextPromptWithInputItem } from '@/app/types';
import {
  getDefaultRecaptionPrompt,
  getProModeTitle,
  type ProTextTranslationFn,
  parseTextToTextPromptWithReference,
  stringifyTextPromptWithReference,
} from '@/app/utils';
import type { PresetPromptValues } from './components/ProTextarea/types';

/**
 * 将 TextPromptWithInputItem 对象 或 纯文本 转换为 ProTextPrompt 对象
 * (打开 Pro Mode / 粘贴)
 *
 * @param basicTextPrompt - 要转换的 TextPromptWithInputItem 对象 或纯文本
 * @param referencePrompts - 参考列表（用于恢复 TextPromptWithInputItem 中的 mention node）
 * @param t - 国际化函数
 * @returns 转换后的 ProTextPrompt 对象
 */
export const convertSimpleToProTextPrompt = (
  basicTextPrompt: TextPromptWithInputItem | string,
  referencePrompts: ReferencePrompt[] | undefined,
  t: ProTextTranslationFn,
): RecaptionPrompt => {
  if (typeof basicTextPrompt === 'string') {
    return convertPlainTextToProTextPrompt(basicTextPrompt, referencePrompts, t);
  }

  return convertTextPromptWithReferenceToProTextPrompt(basicTextPrompt, referencePrompts, t);
};

/**
 * 解析设置项
 *
 * @param key - 设置项的键
 * @param value - 设置项的值
 * @param result - 结果对象
 */
const parsePresetValue = (key: keyof PresetPromptValues, value: string, result: Partial<RecaptionPrompt>): void => {
  const parsedValues = value
    .split(/[,，]/) // 支持英文和中文逗号
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  if (parsedValues.length > 0) {
    // 如果已经存在该 key 的值，合并而不是覆盖
    const existingValues = result[key];
    if (existingValues && Array.isArray(existingValues)) {
      result[key] = [...existingValues, ...parsedValues];
    } else {
      result[key] = parsedValues;
    }
  }
};

/**
 * 将本地化的键映射到对应的 JSON 键
 * 例如：将"运动幅度"、"Motion Level"、"motion level" 等本地化形式统一映射到 "motionLevel"
 *
 * @param localizedKey - 本地化键（会在函数内部进行标准化处理）
 * @param t - 国际化函数
 * @returns 对应的 JSON 键，如果无法映射则返回 null
 */
const mapLocalizedKeyToJsonKey = (localizedKey: string, t: ProTextTranslationFn): keyof RecaptionPrompt | null => {
  const normalizedInputKey = localizedKey.toLowerCase().replace(/\s+/g, '');

  const validJsonKeys: Array<keyof RecaptionPrompt> = [
    'subject',
    'environment',
    'style',
    'motionLevel',
    'shotSize',
    'focalLength',
    'composition',
    'cameraAngle',
    'cameraMovement',
    'specialFilmingTechnique',
  ];

  for (const jsonKey of validJsonKeys) {
    // 先检查是否直接匹配原始英文键名
    if (normalizedInputKey === jsonKey.toLowerCase()) {
      return jsonKey;
    }

    // 再检查是否匹配本地化的键名
    const localizedTitle = getProModeTitle(jsonKey, t);
    const normalizedLocalizedTitle = localizedTitle.toLowerCase().replace(/\s+/g, '');

    if (normalizedInputKey === normalizedLocalizedTitle) {
      return jsonKey;
    }
  }

  return null;
};

/**
 * 直接将 TextPromptWithInputItem 对象转换为 ProTextPrompt 对象，
 * 保留原有的 mention 节点信息
 *
 * @param textPromptWithReference - TextPromptWithInputItem 对象
 * @param referencePrompts - 参考列表
 * @param t - 国际化函数
 * @param locale - 语言类型，默认值为 'en'
 * @returns 转换后的 ProTextPrompt 对象
 */
const convertTextPromptWithReferenceToProTextPrompt = (
  textPromptWithReference: TextPromptWithInputItem,
  referencePrompts?: ReferencePrompt[],
  t?: ProTextTranslationFn,
): RecaptionPrompt => {
  // 初始化结果对象，不包含默认设置
  const result = {
    subject: [],
    environment: [],
    style: [],
    motionLevel: [],
  } as RecaptionPrompt;

  // 收集无法识别的键值对
  const unrecognizedValues: string[] = [];

  // 先获取设置项
  const fullText = stringifyTextPromptWithReference(textPromptWithReference);
  const lines = fullText.split('\n');

  // 提取设置项（非subject/environment）
  for (const line of lines) {
    const trimmedLine = line.trim();
    if (!trimmedLine) continue;

    // 支持英文和中文冒号
    const colonIndex = Math.max(trimmedLine.indexOf(':'), trimmedLine.indexOf('：'));

    if (colonIndex > 0) {
      const rawKey = trimmedLine.substring(0, colonIndex).trim();
      const jsonKey = t ? mapLocalizedKeyToJsonKey(rawKey, t) : null;

      // 只处理非subject/environment的设置项
      if (jsonKey !== 'subject' && jsonKey !== 'environment') {
        if (jsonKey) {
          const value = trimmedLine.substring(colonIndex + 1).trim();
          // 去除可能存在的mention标记 [@xxx]
          const cleanValue = value.replace(/\[@[^\]]+\]/g, '').trim();
          parsePresetValue(jsonKey, cleanValue, result);
        } else {
          // 无法识别的键值对，保存起来
          unrecognizedValues.push(trimmedLine);
        }
      }
    }
  }

  // 状态跟踪变量
  const subjectNodes: TextPromptWithInputItem = [];
  const environmentNodes: TextPromptWithInputItem = [];
  let currentSection: 'subject' | 'environment' | null = null;
  // 收集非 subject 和 environment 区域的 mention 节点
  const orphanedMentions: TextPromptWithInputItem = [];

  // 处理每个节点
  for (let i = 0; i < textPromptWithReference.length; i++) {
    const node = textPromptWithReference[i];

    if (node.type === 'text') {
      // 拆分文本行进行精细处理
      const lines = node.text.split('\n');
      const remainingLines: string[] = [];

      // 逐行处理文本
      for (let j = 0; j < lines.length; j++) {
        const line = lines[j];
        const isSubjectLine = line.match(/^(subject|主体)[：:]/i);
        const isEnvironmentLine = line.match(/^(environment|环境)[：:]/i);

        if (isSubjectLine) {
          // 开始新的subject部分
          currentSection = 'subject';

          // 提取冒号后的内容，去掉前导空格但保留尾部空格
          const colonIndex = Math.max(line.indexOf(':'), line.indexOf('：'));
          if (colonIndex > 0) {
            const content = line.substring(colonIndex + 1).replace(/^\s+/, '');
            if (content) {
              subjectNodes.push({ type: 'text', text: content });
            }
          }
        } else if (isEnvironmentLine) {
          // 开始新的environment部分
          currentSection = 'environment';

          // 提取冒号后的内容，去掉前导空格但保留尾部空格
          const colonIndex = Math.max(line.indexOf(':'), line.indexOf('：'));
          if (colonIndex > 0) {
            const content = line.substring(colonIndex + 1).replace(/^\s+/, '');
            if (content) {
              environmentNodes.push({ type: 'text', text: content });
            }
          }
        } else if (
          line.match(
            /^(style|风格|motion level|运动幅度|shot size|景别|focal length|焦距|composition|构图|camera angle|视角|camera movement|运镜|special filming technique|特殊拍摄)[：:]/i,
          )
        ) {
          // 如果是设置项，不添加到节点中
          // 这些已经在前面处理过
          currentSection = null;
        } else {
          // 常规内容行，添加到当前部分
          if (j === 0 && currentSection) {
            // 如果是节点的第一行，可能需要连接到前一个内容
            if (currentSection === 'subject' && subjectNodes.length > 0) {
              const lastNode = subjectNodes[subjectNodes.length - 1];
              // 如果前一个节点是文本节点，合并内容
              if (lastNode.type === 'text') {
                lastNode.text += ` ${line}`;
              } else {
                // 否则添加为新节点
                subjectNodes.push({ type: 'text', text: line });
              }
            } else if (currentSection === 'environment' && environmentNodes.length > 0) {
              const lastNode = environmentNodes[environmentNodes.length - 1];
              if (lastNode.type === 'text') {
                lastNode.text += ` ${line}`;
              } else {
                environmentNodes.push({ type: 'text', text: line });
              }
            } else if (currentSection === 'subject') {
              subjectNodes.push({ type: 'text', text: line });
            } else if (currentSection === 'environment') {
              environmentNodes.push({ type: 'text', text: line });
            }
          } else if (currentSection) {
            // 节点的后续行直接添加到剩余行
            remainingLines.push(line);
          }
        }
      }

      // 处理剩余行
      if (remainingLines.length > 0 && currentSection) {
        const remainingText = remainingLines.join('\n');
        if (currentSection === 'subject') {
          subjectNodes.push({ type: 'text', text: remainingText });
        } else if (currentSection === 'environment') {
          environmentNodes.push({ type: 'text', text: remainingText });
        }
      }
    } else if (node.type === 'mention') {
      // mention节点添加到当前部分
      if (currentSection === 'subject') {
        subjectNodes.push(node);
      } else if (currentSection === 'environment') {
        environmentNodes.push(node);
      }
      // 如果没有当前部分，查看前后文本节点来确定
      else if (i > 0 && i < textPromptWithReference.length - 1) {
        // 查看前一个节点
        const prevNode = textPromptWithReference[i - 1];
        if (prevNode.type === 'text') {
          if (prevNode.text.match(/(subject|主体)[：:]/i)) {
            subjectNodes.push(node);
            currentSection = 'subject';
          } else if (prevNode.text.match(/(environment|环境)[：:]/i)) {
            environmentNodes.push(node);
            currentSection = 'environment';
          }
        }

        // 未匹配时，尝试查看后一个节点
        if (currentSection === null) {
          const nextNode = textPromptWithReference[i + 1];
          if (nextNode?.type === 'text') {
            const nextText = nextNode.text;
            // 检查下一个节点的第一行是否有section标识
            const firstLine = nextText.split('\n')[0];

            // 如果下一个节点第一行没有section标识，继承前面节点的section
            if (!firstLine.match(/(subject|主体|environment|环境|style|风格|motion level|运动幅度)[：:]/i)) {
              // 查找下一个section标识前的内容应该属于哪个部分
              if (nextText.indexOf('environment:') > 0 || nextText.indexOf('环境:') > 0) {
                subjectNodes.push(node);
                currentSection = 'subject';
              }
            }
          }
        }

        // 仍然未能确定位置，添加到孤立的mention节点列表
        if (currentSection === null) {
          orphanedMentions.push(node);
        }
      } else {
        // 没有上下文可判断的mention节点，添加到孤立的mention节点列表
        orphanedMentions.push(node);
      }
    }
  }

  // 合并结果
  if (subjectNodes.length > 0) {
    result.subject = subjectNodes;
  }

  if (environmentNodes.length > 0) {
    result.environment = environmentNodes;
  }

  // 将孤立的mention节点添加到subject末尾
  if (orphanedMentions.length > 0) {
    if (result.subject.length > 0) {
      result.subject = [...result.subject, ...orphanedMentions];
    } else {
      result.subject = orphanedMentions;
    }
  }

  // 将无法识别的键值对添加到 subject 末尾
  if (unrecognizedValues.length > 0 && result.subject.length > 0) {
    const lastSubjectNode = result.subject[result.subject.length - 1];
    if (lastSubjectNode.type === 'text') {
      lastSubjectNode.text += `, ${unrecognizedValues.join(', ')}`;
    } else {
      // 如果最后一个节点不是文本节点，添加一个新的文本节点
      result.subject.push({
        type: 'text',
        text: unrecognizedValues.join(', '),
      });
    }
  } else if (unrecognizedValues.length > 0 && result.subject.length === 0) {
    // 如果 subject 为空，创建一个新的文本节点
    result.subject = [
      {
        type: 'text',
        text: unrecognizedValues.join(', '),
      },
    ];
  }

  // 如果没有成功提取到节点，回退到使用文本解析
  if (result.subject.length === 0 && result.environment.length === 0) {
    return convertPlainTextToProTextPrompt(fullText, referencePrompts, t);
  }

  // 应用默认值（仅当用户没有提供时）
  if (t) {
    const defaultValues = getDefaultRecaptionPrompt(t);
    if (result.style.length === 0) {
      result.style = defaultValues.style;
    }
    if (result.motionLevel.length === 0) {
      result.motionLevel = defaultValues.motionLevel;
    }
  }

  return result;
};

/**
 * 纯文本转换为 ProTextPrompt 对象
 *
 * @param text - 纯文本
 * @param referencePrompts - 参考列表（用于恢复 TextPromptWithInputItem 中的 mention node）
 * @param t - 国际化函数
 * @param locale - 语言类型，默认值为 'en'
 * @returns
 */
const convertPlainTextToProTextPrompt = (
  text: string,
  referencePrompts?: ReferencePrompt[],
  t?: ProTextTranslationFn,
): RecaptionPrompt => {
  // 初始化结果对象，不包含默认设置
  const result = {
    subject: '',
    environment: '',
    preset: {
      style: [],
      motionLevel: [],
    } as Pick<RecaptionPrompt, 'style' | 'motionLevel'>, // 先创建一个空的设置对象
  };

  const remainingParts: string[] = [];
  let subjectFound = false;
  let environmentFound = false;

  const lines = text.split('\n');

  // 处理提取 subject 和 environment
  let currentSection: null | 'subject' | 'environment' = null;
  let currentContent: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const trimmedLine = lines[i].trim();
    if (!trimmedLine) continue;

    // Support both standard and Chinese colons
    const colonIndex = Math.max(trimmedLine.indexOf(':'), trimmedLine.indexOf('：'));

    if (colonIndex > 0) {
      // 检查是否是新的键值对
      const rawKey = trimmedLine.substring(0, colonIndex).trim();
      const value = trimmedLine.substring(colonIndex + 1).trim();
      const jsonKey = t ? mapLocalizedKeyToJsonKey(rawKey, t) : null;

      // 如果遇到一个新的 section，先处理之前的内容
      if (currentSection && currentContent.length > 0) {
        result[currentSection] = currentContent.join('\n');
        currentContent = [];
      }

      let matchedSetting = false;

      if (jsonKey === 'subject') {
        if (!subjectFound) {
          currentSection = 'subject';
          if (value) currentContent.push(value);
          subjectFound = true;
        } else {
          remainingParts.push(trimmedLine);
        }
        matchedSetting = true;
      } else if (jsonKey === 'environment') {
        if (!environmentFound) {
          // 如果之前处理的是 subject，先保存 subject 内容
          if (currentSection === 'subject' && currentContent.length > 0) {
            result.subject = currentContent.join('\n');
            currentContent = [];
          }

          currentSection = 'environment';
          if (value) currentContent.push(value);
          environmentFound = true;
        } else {
          remainingParts.push(trimmedLine);
        }
        matchedSetting = true;
      } else {
        // 如果遇到设置项，先保存当前处理的 section
        if (currentSection && currentContent.length > 0) {
          result[currentSection] = currentContent.join('\n');
          currentContent = [];
          currentSection = null;
        }

        // 检查是否是设置项
        if (jsonKey) {
          parsePresetValue(jsonKey, value, result.preset);
          matchedSetting = true;
        }
      }

      if (!matchedSetting) {
        // 未匹配的键值对，直接放入 remainingParts
        remainingParts.push(trimmedLine);
      }
    } else {
      // 非键值对行，如果当前在处理某个 section，将其添加到该 section
      if (currentSection) {
        currentContent.push(trimmedLine);
      } else {
        // Line without a colon (or colon is the first char)
        remainingParts.push(trimmedLine);
      }
    }
  }

  // 处理最后一个 section
  if (currentSection && currentContent.length > 0) {
    result[currentSection] = currentContent.join('\n');
  }

  if (remainingParts.length > 0) {
    const remainingText = remainingParts.join(', ');
    if (result.subject) {
      result.subject = `${result.subject}, ${remainingText}`;
    } else {
      result.subject = remainingText;
    }
  }

  // 应用默认值（仅当用户没有提供时）
  if (t) {
    const defaultValues = getDefaultRecaptionPrompt(t);
    if (result.preset.style.length === 0) {
      result.preset.style = defaultValues.style;
    }
    if (result.preset.motionLevel.length === 0) {
      result.preset.motionLevel = defaultValues.motionLevel;
    }
  }

  // 将设置项合并入结果中
  const finalResult: RecaptionPrompt = {
    ...result.preset,
    subject: parseTextToTextPromptWithReference(result.subject, referencePrompts),
    environment: parseTextToTextPromptWithReference(result.environment, referencePrompts),
  };

  return finalResult;
};
